import React from "react";

import "./style.scss";

export const BRIEFING_SKELETON_ROW_COUNT = 8;

export type BriefingSkeletonSection = {
  id: string;
  name: string;
};

type BriefingMatrixSkeletonProps = {
  sections: BriefingSkeletonSection[];
  caption?: string;
  ariaLabel?: string;
  showActions?: boolean;
};

/**
 * Loading matrix used by Public Data (WatchlistSummary) and Business Health.
 * Same pulsing section dots and brief-line placeholders.
 */
const BriefingMatrixSkeleton: React.FC<BriefingMatrixSkeletonProps> = ({
  sections,
  caption = "Generating intelligence briefings for public data.",
  ariaLabel = "Generating intelligence briefings for public data.",
  showActions = true,
}) => {
  const columnCount = 2 + sections.length + (showActions ? 1 : 0);

  return (
    <div
      className="watchlist-summary-matrix briefing-matrix-skeleton pb-8 pt-4"
      aria-busy="true"
      aria-live="polite"
      aria-label={ariaLabel}
    >
      <p className="briefing-skeleton-caption">{caption}</p>
      <div className="watchlist-summary-card">
        <div className="watchlist-summary-card-content">
          <div className="table-container">
            <table className="summary-table">
              <thead>
                <tr>
                  <th className="header-cell account-header">
                    <div className="header-content">
                      <span>Account</span>
                    </div>
                  </th>
                  <th className="header-cell overall-header">
                    <div className="header-content">
                      <span>Overall</span>
                    </div>
                  </th>
                  {sections.map(s => (
                    <th key={s.id} className="header-cell section-header">
                      <div className="header-content">
                        <span>{s.name}</span>
                      </div>
                    </th>
                  ))}
                  {showActions ? (
                    <th className="header-cell actions-header" aria-hidden />
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {Array.from(
                  { length: BRIEFING_SKELETON_ROW_COUNT },
                  (_, row) => (
                    <React.Fragment key={row}>
                      <tr className="table-row briefing-skeleton-row">
                        <td className="account-cell">
                          <div className="skeleton-account-block">
                            <div className="skeleton-icon skeleton-pulse" />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="skeleton-account-name skeleton-pulse" />
                            </div>
                          </div>
                        </td>
                        <td className="data-cell overall-cell">
                          <div className="skeleton-metric-wrap">
                            <div className="skeleton-metric-track" />
                            <div className="skeleton-metric-dot skeleton-pulse" />
                          </div>
                        </td>
                        {sections.map(s => (
                          <td key={s.id} className="data-cell">
                            <div className="skeleton-metric-wrap">
                              <div className="skeleton-metric-track" />
                              <div className="skeleton-metric-dot skeleton-pulse" />
                            </div>
                          </td>
                        ))}
                        {showActions ? (
                          <td className="actions-cell">
                            <div className="skeleton-actions">
                              <div className="skeleton-action-btn skeleton-pulse" />
                              <div className="skeleton-action-btn skeleton-pulse" />
                            </div>
                          </td>
                        ) : null}
                      </tr>
                      <tr className="table-row briefing-skeleton-row briefing-skeleton-brief-row">
                        <td
                          colSpan={columnCount}
                          className="account-brief-line-cell"
                        >
                          <div className="skeleton-brief-block skeleton-pulse" />
                        </td>
                      </tr>
                    </React.Fragment>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BriefingMatrixSkeleton;
